/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link, useRouter, useTranslations } from "@i18n/next";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import type { SubscriptionDetail } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  createModal,
  cssMediaQueryMinWidthTablet,
  FullscreenModalModal,
  radius,
  spacing,
  Text,
  useModal,
} from "@packages/ui";
import { differenceInCalendarDays } from "date-fns";
import Image from "next/image";

import Harold from "./assets/harold.gif";

const IN_SAFARI = "@supports (hanging-punctuation: first)";

const modalStyles: Interpolation = {
  alignItems: "center",
  display: "grid",
  gap: spacing.m,
  gridAutoRows: "min-content",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "min-content",
  justifyContent: "center",
  maxWidth: 480,
  overflow: "auto",
  padding: spacing.l,
  alignContent: "center",
  borderRadius: radius.none,
  [cssMediaQueryMinWidthTablet]: {
    alignContent: "center",
    [IN_SAFARI]: { alignContent: "start" },
    borderRadius: radius.l,
    padding: spacing.l,
  },
};

const imageStyles: Interpolation = {
  height: "auto",
  maxHeight: 200,
  objectFit: "contain",
  objectPosition: "center",
  width: spacing.full,
};

const headingsStyles: Interpolation = {
  textAlign: "center",
};

const subheadingStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsHigher,
});

const sectionStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  justifyContent: "flex-start",
  width: spacing.full,
};

const textStyles: Interpolation = {
  paddingBlock: spacing.s,
};

const buttonsStyles: Interpolation = {
  paddingTop: spacing.m,
};
export type GiftRedeemedModalProps = {
  subscription: SubscriptionDetail;
};

const SCREEN = "gift_sub_redeemed_confirmation";

export const GiftRedeemedModal = createModal(
  ({ subscription }: GiftRedeemedModalProps) => {
    const modal = useModal();
    const router = useRouter();
    const t = useTranslations();
    const { track } = useAnalytics();

    useViewedScreen({
      screen: SCREEN,
      isReady: modal.visible,
    });

    return (
      <FullscreenModalModal
        open={modal.visible}
        onOpenChange={(open) => {
          if (open) modal.show();
          else {
            modal.resolve(false);
            modal.remove();
          }
        }}
        modalProps={{
          contentProps: {
            onInteractOutside: (e) => e.preventDefault(),
          },
          overlayProps: {
            onClick: () => {
              modal.resolve(false);
              modal.remove();
              router.push("/home");
            },
          },
          contentCSS: modalStyles,
          showClose: true,
        }}
        fullScreenModalProps={{
          contentProps: { onInteractOutside: (e) => e.preventDefault() },
          contentCSS: modalStyles,
          showCloseButton: true,
        }}
      >
        <Image src={Harold} alt={""} placeholder={"blur"} css={imageStyles} />
        <div css={[sectionStyles, textStyles]}>
          <Text as={"h1"} type={"headline"} size={"s"} css={headingsStyles}>
            {t("gift_sub_success_title")}
          </Text>
          <Text
            type={"body"}
            size={"m"}
            as={"p"}
            css={[headingsStyles, subheadingStyles]}
          >
            {t("gift_sub_success_subtitle_num_days", {
              "0": differenceInCalendarDays(
                new Date(subscription.trial_ends_at),
                new Date(),
              ),
            })}
          </Text>
        </div>
        <div css={[sectionStyles, buttonsStyles]}>
          <Button
            asChild
            isFullWidth
            size={"l"}
            onClickCapture={() => {
              track({
                event: HallowAnalyticsEvent.TappedExplorePrayers,
                properties: {
                  screen_name: SCREEN,
                },
              });
              modal.resolve(true);
              modal.remove();
            }}
          >
            <Link href={"/home"}>{t("gift_sub_explore_prayers")}</Link>
          </Button>
          <Button
            asChild
            isFullWidth
            size={"l"}
            variant={"transparentNeutral"}
            onClickCapture={() => {
              track({
                event: HallowAnalyticsEvent.TappedReviewInSettings,
                properties: {
                  screen_name: SCREEN,
                },
              });
              modal.resolve(true);
              modal.remove();
            }}
          >
            <Link href={"/settings/subscription"}>{t("review_settings")}</Link>
          </Button>
        </div>
      </FullscreenModalModal>
    );
  },
);
